import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useQuery } from '@tanstack/react-query';

import { BillingService, type CreditPack } from '../services/billingService';
import { queryKeys } from '../queries/keys';
import { formatNgn, type CreditShortfall } from './insufficientCredits';

/** How often we ask whether the money has landed, and when to stop asking. */
const POLL_MS = 2_000;
const GIVE_UP_MS = 15 * 60 * 1_000;

type Phase = 'choose' | 'waiting' | 'funded';

interface Props {
  open: boolean;
  /** Set when a refused operation is behind this; sizes the recommendation. */
  shortfall: CreditShortfall | null;
  balanceNgn: number;
  /** True when paying will resume something — worth promising explicitly. */
  willResumeWork: boolean;
  onFunded: () => void;
  onClose: () => void;
}

/**
 * Top-up, without leaving your work.
 *
 * Checkout is the payment provider's own hosted page, so it cannot be embedded —
 * and sending the browser there would unmount the canvas and lose everything in
 * it. Instead it opens in a second tab while this drawer stays put and watches
 * the balance. When the money lands, the blocked operation resumes by itself.
 */
export const TopUpDrawer: React.FC<Props> = ({
  open,
  shortfall,
  balanceNgn,
  willResumeWork,
  onFunded,
  onClose,
}) => {
  const [phase, setPhase] = useState<Phase>('choose');
  const [error, setError] = useState<string | null>(null);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [popupBlocked, setPopupBlocked] = useState(false);

  // The balance we started from. Funding is "it went up", which is robust to
  // partial payments and to a webhook that credits a different amount than the
  // pack we clicked — we react to money arriving, not to our own assumptions.
  const baseline = useRef(balanceNgn);
  const startedAt = useRef(0);

  // Mirrored so the open-reset effect can read the current balance without
  // depending on it — depending on it would re-run the reset the moment the
  // balance moves, which is precisely the event the waiting state exists for.
  const latestBalance = useRef(balanceNgn);
  latestBalance.current = balanceNgn;

  const packsQuery = useQuery({
    queryKey: queryKeys.creditPacks(),
    queryFn: () => BillingService.getPacks(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!open) return;
    setPhase('choose');
    setError(null);
    setCheckoutUrl(null);
    setPopupBlocked(false);
    baseline.current = latestBalance.current;
  }, [open]);

  /** Poll while waiting, and check immediately whenever the user tabs back. */
  useEffect(() => {
    if (!open || phase !== 'waiting') return;

    let cancelled = false;

    const check = async () => {
      try {
        // Reconcile first. The webhook is the fast path, but if it never arrives the user
        // sits here watching a spinner having already paid — so each poll also asks the
        // server to check with the provider directly. Idempotent, so the two can race.
        await BillingService.sync().catch(() => undefined);

        const fresh = await BillingService.getBalance();
        if (cancelled) return;
        if (fresh.balanceNgn > baseline.current) {
          setPhase('funded');
          // A beat on the confirmation, so it registers as "paid" rather than
          // the drawer just vanishing.
          setTimeout(onFunded, 1_200);
          return;
        }
        if (Date.now() - startedAt.current > GIVE_UP_MS) {
          setPhase('choose');
          setError('We haven’t seen this payment yet. If you completed it, your credit will appear shortly.');
        }
      } catch {
        // Transient failures are expected while polling; the next tick retries.
      }
    };

    const timer = window.setInterval(check, POLL_MS);
    window.addEventListener('focus', check);
    void check();

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', check);
    };
  }, [open, phase, onFunded]);

  const buy = useCallback(async (pack: CreditPack) => {
    setError(null);
    setPopupBlocked(false);

    // Open the tab NOW, inside the click. Browsers block window.open once an
    // await has broken the user-gesture chain, so the tab has to exist before
    // we know where it's going — we point it at the URL when it arrives.
    const tab = window.open('', '_blank');

    try {
      const started = await BillingService.startPurchase(pack.id);
      setCheckoutUrl(started.checkoutUrl);
      startedAt.current = Date.now();

      if (tab && !tab.closed) {
        tab.location.href = started.checkoutUrl;
      } else {
        // Blocked, or opened in a browser that refuses blank tabs. Offer the
        // link rather than silently doing nothing.
        setPopupBlocked(true);
      }
      setPhase('waiting');
    } catch (err) {
      tab?.close();
      setError(err instanceof Error ? err.message : 'Could not start the payment.');
    }
  }, []);

  const packs = packsQuery.data;
  const available = packs?.purchaseAvailable ?? false;

  // The smallest pack that clears the shortfall — the obvious default, without
  // nudging anyone towards the biggest one.
  const recommendedId =
    shortfall && packs
      ? packs.packs.find((p) => p.ngn >= shortfall.shortfallNgn)?.id ?? packs.packs.at(-1)?.id
      : undefined;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, p: 3 } }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h6" fontWeight={700}>
          {shortfall ? 'Add credit to continue' : 'Add credit'}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {shortfall && phase === 'choose' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This costs {formatNgn(shortfall.requiredNgn)} and you have{' '}
          {formatNgn(shortfall.balanceNgn)}.
          {willResumeWork && ' We’ll pick up where you left off once you’ve topped up.'}
        </Alert>
      )}

      {!shortfall && phase === 'choose' && (
        <Typography variant="body2" color="text.secondary" mb={2}>
          Balance {formatNgn(balanceNgn)}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {phase === 'choose' && (
        <>
          {packsQuery.isLoading && (
            <Stack alignItems="center" py={4}>
              <CircularProgress size={24} />
            </Stack>
          )}

          {packs && !available && (
            <Alert severity="warning">
              {'Card payments aren’t available here yet. You can still use your own provider ' +
                'keys — add them in Settings and your usage won’t be charged.'}
            </Alert>
          )}

          {packs && available && (
            <Stack spacing={1.5}>
              {packs.packs.map((pack) => {
                const recommended = pack.id === recommendedId;
                return (
                  <Button
                    key={pack.id}
                    onClick={() => void buy(pack)}
                    variant={recommended ? 'contained' : 'outlined'}
                    size="large"
                    sx={{ justifyContent: 'space-between', px: 2, py: 1.5, textTransform: 'none' }}
                  >
                    <Stack alignItems="flex-start">
                      <Typography fontWeight={700}>{formatNgn(pack.ngn)}</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.75 }}>
                        {pack.name}
                        {recommended ? ' · covers this' : ''}
                      </Typography>
                    </Stack>
                    <OpenInNewIcon fontSize="small" sx={{ opacity: 0.6 }} />
                  </Button>
                );
              })}
              <Typography variant="caption" color="text.secondary" sx={{ pt: 1 }}>
                Payment opens in a new tab. Keep this one open — your work stays exactly as it is.
              </Typography>
            </Stack>
          )}
        </>
      )}

      {phase === 'waiting' && (
        <Stack spacing={2} alignItems="center" py={4} textAlign="center">
          <CircularProgress size={32} />
          <Typography fontWeight={600}>Waiting for your payment…</Typography>
          <Typography variant="body2" color="text.secondary">
            {'Finish checkout in the other tab. This updates by itself — you don’t need ' +
              'to refresh.'}
          </Typography>

          {popupBlocked && checkoutUrl && (
            <Alert severity="warning" sx={{ textAlign: 'left' }}>
              Your browser blocked the payment tab.{' '}
              <Link href={checkoutUrl} target="_blank" rel="noopener noreferrer">
                Open checkout
              </Link>
            </Alert>
          )}

          <Divider flexItem />
          <Button size="small" onClick={onClose}>
            Cancel
          </Button>
        </Stack>
      )}

      {phase === 'funded' && (
        <Stack spacing={1.5} alignItems="center" py={5} textAlign="center">
          <CheckCircleIcon color="success" sx={{ fontSize: 44 }} />
          <Typography fontWeight={700}>Credit added</Typography>
          <Typography variant="body2" color="text.secondary">
            {willResumeWork ? 'Picking up where you left off…' : 'You’re good to go.'}
          </Typography>
        </Stack>
      )}
    </Drawer>
  );
};
